import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} [addToCartButton] - Sticky bar's button
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    const productForm = this.#getProductForm();
    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const targetSection = productForm?.closest('.shopify-section');
    targetSection?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    targetSection?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;
    this.#buyButtonsBlock = buyButtonsBlock;

    const footer = document.querySelector('footer');

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      const hasScrolledPastBuyButtons = entry.boundingClientRect.top < 0;
      const shouldShowSticky = !entry.isIntersecting && hasScrolledPastBuyButtons && !this.#hiddenByBottom;

      if (shouldShowSticky) {
        this.#showStickyBar();
        return;
      }

      if (this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    if (footer) {
      // Observer for footer visibility - hides sticky bar at page bottom
      this.#mainBottomObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;

          if (entry.isIntersecting) {
            this.#hiddenByBottom = true;
            if (this.#isStuck) {
              this.#hideStickyBar();
            }
            return;
          }

          if (this.#hiddenByBottom) {
            this.#hiddenByBottom = false;
            this.#syncStickyVisibilityToBuyButtons();
          }
        },
        {
          rootMargin: '0px 0px 200px 0px',
        }
      );

      this.#mainBottomObserver.observe(footer);
    }

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = async (event) => {
    if (String(event.detail.data.productId) !== String(this.dataset.productId)) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    let newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) {
      newStickyAddToCart = await this.#fetchStickyAddToCartMarkup(variant?.id);
    }
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  #syncStickyVisibilityToBuyButtons() {
    if (!this.#buyButtonsBlock) return;

    const hasScrolledPastBuyButtons = this.#buyButtonsBlock.getBoundingClientRect().top < 0;
    const shouldShowSticky = hasScrolledPastBuyButtons && !this.#hiddenByBottom;
    if (!shouldShowSticky) return;

    this.#showStickyBar();
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const forms = document.querySelectorAll(`product-form-component[data-product-id="${productId}"]`);
    for (const form of forms) {
      if (form.closest('sticky-add-to-cart')) continue;
      if (!form.closest('.buy-buttons-block')) continue;
      if (!form.querySelector('[ref="addToCartButton"]')) continue;
      return /** @type {HTMLElement} */ (form);
    }

    for (const form of forms) {
      if (form.closest('sticky-add-to-cart')) continue;
      return /** @type {HTMLElement} */ (form);
    }

    return null;
  }

  /**
   * Fetches the latest sticky add-to-cart markup from this section.
   * Needed when sticky ATC lives in a different section than product form.
   * @param {string|number|undefined} variantId - Variant id for server-side section render
   * @returns {Promise<HTMLElement|null>}
   */
  async #fetchStickyAddToCartMarkup(variantId) {
    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    if (!sectionId) return null;

    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    if (variantId) {
      url.searchParams.set('variant', String(variantId));
    }

    try {
      const response = await fetch(url.toString());
      if (!response.ok) return null;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector('sticky-add-to-cart');
    } catch {
      return null;
    }
  }

}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
